import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CustomDatePicker } from '../ui/custom-date-picker';
import { CustomDateTimePicker } from '../ui/custom-datetime-picker';
import { ADVANCE_REASONS, PAYMENT_METHODS, preventWheelChange } from './constants';
import { getTodayString, getCurrentDateTimeString } from '../../utils/dateUtils';

export function EditAdvanceDialog({ open, onClose, advanceData, setAdvanceData, onSubmit, selectedEmployee }) {
  if (!advanceData) return null;

  // Date restrictions
  const minDate = selectedEmployee?.start_date || undefined;
  const maxDate = (() => {
    const today = getTodayString();
    const endDate = selectedEmployee?.end_date || selectedEmployee?.termination_date;
    if (endDate && endDate < today) return endDate;
    return today;
  })();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Edit Advance Record
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Amount *</Label>
            <Input
              type="number"
              value={advanceData.amount || ''}
              onChange={(e) => setAdvanceData({ ...advanceData, amount: e.target.value })}
              onWheel={preventWheelChange}
              required
              className="h-11 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Date *</Label>
            <CustomDatePicker
              value={advanceData.date || ''}
              onChange={(date) => setAdvanceData({ ...advanceData, date: date })}
              placeholder="Select date"
              min={minDate}
              max={maxDate}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Reason *</Label>
            <Select 
              value={advanceData.reason || ''} 
              onValueChange={(v) => setAdvanceData({ ...advanceData, reason: v })}
            >
              <SelectTrigger className="h-11 rounded-xl">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {ADVANCE_REASONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {advanceData.reason === 'other' && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Specify Reason *</Label>
              <Input
                value={advanceData.other_reason || ''}
                onChange={(e) => setAdvanceData({ ...advanceData, other_reason: e.target.value })}
                placeholder="Enter reason"
                required
                className="h-11 rounded-xl"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Payment Method *</Label>
            <Select 
              value={advanceData.payment_method || 'UPI'} 
              onValueChange={(v) => setAdvanceData({ ...advanceData, payment_method: v })}
            >
              <SelectTrigger className="h-11 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Notes</Label>
            <Input
              value={advanceData.notes || ''}
              onChange={(e) => setAdvanceData({ ...advanceData, notes: e.target.value })}
              placeholder="Additional notes"
              className="h-11 rounded-xl"
            />
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => onClose(false)} className="flex-1 h-11 rounded-xl">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl">
              Update Advance
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditOvertimeDialog({ open, onClose, overtimeData, setOvertimeData, onSubmit, selectedEmployee }) {
  if (!overtimeData) return null;

  // Calculate hours from datetime values
  const calculateHours = () => {
    if (overtimeData.start_datetime && overtimeData.end_datetime) {
      const start = new Date(overtimeData.start_datetime);
      const end = new Date(overtimeData.end_datetime);
      if (end > start) {
        return ((end - start) / (1000 * 60 * 60)).toFixed(2);
      }
    }
    return null;
  };

  // Check if end is less than or equal to start
  const isInvalidRange = () => {
    if (overtimeData.start_datetime && overtimeData.end_datetime) {
      const start = new Date(overtimeData.start_datetime);
      const end = new Date(overtimeData.end_datetime);
      return end <= start;
    }
    return false;
  };

  const calculatedHours = calculateHours();
  const invalidRange = isInvalidRange();

  // Date restrictions: no future dates, no dates before start_date
  const minDate = selectedEmployee?.start_date || undefined;
  const maxDate = (() => {
    const today = getTodayString();
    const endDate = selectedEmployee?.end_date || selectedEmployee?.termination_date;
    if (endDate && endDate < today) return endDate;
    return today;
  })();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Edit Overtime Record
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Start Date & Time *</Label>
            <CustomDateTimePicker
              value={overtimeData.start_datetime}
              onChange={(val) => setOvertimeData({ ...overtimeData, start_datetime: val })}
              placeholder="Select start date & time"
              label="Start Time"
              min={minDate}
              max={maxDate}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">End Date & Time *</Label>
            <CustomDateTimePicker
              value={overtimeData.end_datetime}
              onChange={(val) => setOvertimeData({ ...overtimeData, end_datetime: val })}
              placeholder="Select end date & time"
              label="End Time"
              min={minDate}
              max={maxDate}
            />
          </div>
          {invalidRange && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-red-600 font-medium text-sm">
                ⚠️ End date and time should not be less than or equal to start date and time
              </p>
            </div>
          )}
          {calculatedHours && !invalidRange && (
            <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-3">
              <p className="text-emerald-800 font-bold">
                Calculated: <span className="text-xl">{calculatedHours} hrs</span>
                <span className="text-sm font-normal ml-2">
                  ({Math.floor(calculatedHours)} hr {Math.round((calculatedHours % 1) * 60)} min)
                </span>
              </p>
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Notes</Label>
            <Input
              value={overtimeData.notes || ''}
              onChange={(e) => setOvertimeData({ ...overtimeData, notes: e.target.value })}
              placeholder="Additional notes"
              className="h-11 rounded-xl"
            />
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => onClose(false)} className="flex-1 h-11 rounded-xl">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={invalidRange}
              className="flex-1 h-11 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Update Overtime
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
